import type {
  AllocationStatus,
  LeaveStatus,
  LeaveType,
  ReservationType,
} from "../../shared/types/api";
import type { Tone } from "../../shared/ui";

/** How resource-planning vocabulary reads, in one place so every screen agrees. */

export const ALLOCATION_STATUS_LABELS: Record<AllocationStatus, string> = {
  PLANNED: "Planned",
  CONFIRMED: "Confirmed",
  COMPLETED: "Completed",
  CANCELLED: "Cancelled",
};

export const ALLOCATION_STATUS_TONE: Record<AllocationStatus, Tone> = {
  PLANNED: "gray",
  CONFIRMED: "green",
  COMPLETED: "blue",
  CANCELLED: "gray",
};

export const LEAVE_STATUS_LABELS: Record<LeaveStatus, string> = {
  REQUESTED: "Requested",
  APPROVED: "Approved",
  REJECTED: "Rejected",
  CANCELLED: "Cancelled",
};

export const LEAVE_STATUS_TONE: Record<LeaveStatus, Tone> = {
  REQUESTED: "amber",
  APPROVED: "green",
  REJECTED: "red",
  CANCELLED: "gray",
};

export const LEAVE_TYPE_LABELS: Record<LeaveType, string> = {
  ANNUAL: "Annual leave",
  SICK: "Sick leave",
  UNPAID: "Unpaid leave",
  PARENTAL: "Parental leave",
  COMPASSIONATE: "Compassionate leave",
  OTHER: "Other",
};

export const RESERVATION_TYPE_LABELS: Record<ReservationType, string> = {
  TRAINING: "Training",
  INTERNAL_MEETINGS: "Internal meetings",
  ADMIN: "Admin time",
  SUPPORT_ROTA: "Support rota",
  OTHER: "Other",
};

export function formatHours(hours: number | null | undefined): string {
  if (hours == null) {
    return "—";
  }
  const rounded = Math.round(hours * 10) / 10;
  return `${rounded.toLocaleString(undefined, { maximumFractionDigits: 1 })}h`;
}

export function formatPercent(fraction: number): string {
  return `${Math.round(fraction * 100)}%`;
}

/**
 * How a utilisation figure should read (§42, §43).
 *
 * <p>Bands rather than a single threshold. Ninety-five per cent and a hundred and forty are both
 * "not exactly 100%" and mean entirely different things, and a screen that flagged the first as a
 * problem would be ignored by the time the second appeared.
 */
export function describeUtilisation(
  utilisation: number,
  unavailable: boolean,
): { tone: Tone; label: string } {
  if (unavailable) {
    return { tone: "gray", label: "Away" };
  }
  if (utilisation === 0) {
    return { tone: "gray", label: "Unassigned" };
  }
  if (utilisation < 0.6) {
    // §43: meaningfully idle, not merely under a hundred per cent.
    return { tone: "blue", label: "Room to spare" };
  }
  if (utilisation <= 1.05) {
    return { tone: "green", label: "Well used" };
  }
  if (utilisation <= 1.25) {
    return { tone: "amber", label: "Over capacity" };
  }
  return { tone: "red", label: "Badly over capacity" };
}

/**
 * The colour of a heatmap cell (§45).
 *
 * <p>A deliberately coarse scale. The grid is read at a glance to find the trouble, and a smooth
 * gradient makes 95% and 105% look nearly identical — which is precisely the boundary somebody is
 * scanning for. Absence gets its own treatment rather than reading as idle, because a person
 * who is away is not available to be given work.
 */
export function heatmapClasses(cell: {
  utilisation: number;
  unavailable: boolean;
  availableHours: number;
}): string {
  if (cell.unavailable) {
    return "bg-ink-200 text-ink-500";
  }
  if (cell.availableHours === 0) {
    return "bg-ink-100 text-ink-400";
  }
  if (cell.utilisation === 0) {
    return "bg-ink-50 text-ink-400";
  }
  if (cell.utilisation < 0.6) {
    return "bg-brand-100 text-brand-700";
  }
  if (cell.utilisation <= 1.05) {
    return "bg-emerald-100 text-emerald-700";
  }
  if (cell.utilisation <= 1.25) {
    return "bg-amber-100 text-amber-700";
  }
  return "bg-red-100 text-red-700";
}

/** A window covering the next four weeks, which is what a planner opens on. */
export function defaultWindow(): { from: string; to: string } {
  const today = new Date();
  const start = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
  // Back to Monday, so the weekly buckets line up with how people talk about their calendar.
  const daysSinceMonday = (start.getUTCDay() + 6) % 7;
  start.setUTCDate(start.getUTCDate() - daysSinceMonday);

  const end = new Date(start);
  end.setUTCDate(end.getUTCDate() + 27);

  return { from: iso(start), to: iso(end) };
}

function iso(date: Date): string {
  return date.toISOString().slice(0, 10);
}
